/**
 * FirebaseScoreService - Handles score storage and retrieval using Firebase
 * 
 * This service implements multi-member account functionality where each authenticated user
 * can have multiple members within their account, each with their own scores.
 * Leaderboards are account-scoped (private to each account).
 */

import { doc, getDoc, setDoc, updateDoc, DocumentData } from 'firebase/firestore';
import { db, auth } from '../firebase/firebase';
import { User } from 'firebase/auth';

interface Member {
  name: string;
  createdAt: Date;
  isActive: boolean;
  icon?: string;
}

interface Score {
  currentScore: number;
  lastUpdated: Date;
}

interface LeaderboardEntry {
  memberId: string;
  name: string;
  score: number;
  lastUpdated: Date;
  rank?: number;
}

interface Account {
  accountInfo: {
    email: string;
    displayName: string;
    createdAt: Date;
  };
  members: { [key: string]: Member | null };
  scores: { [key: string]: Score };
  leaderboard: LeaderboardEntry[];
  activeMemberId: string | null;
  leaderboardUpdated?: Date;
}

class FirebaseScoreService {
  /**
   * Get the current score for a specific member
   */
  static async getScore(memberId: string | null = null): Promise<number> {
    try {
      const user = auth.currentUser;
      
      // If no user is signed in, return default score
      if (!user) {
        console.warn('No user signed in, returning default score');
        return 0;
      }
      
      // If no memberId provided, try to get the active member or default member
      if (!memberId) {
        const account = await this.getAccount();
        if (!account || !account.members || Object.keys(account.members).length === 0) {
          return 0;
        }
        
        // Get the active member or first member
        memberId = account.activeMemberId || Object.keys(account.members)[0];
      }
      
      console.log(`Fetching score for user: ${user.uid}, member: ${memberId}`);
      
      // Get the account document
      const accountRef = doc(db, 'accounts', user.uid);
      const accountDoc = await getDoc(accountRef);
      
      if (accountDoc.exists()) {
        const accountData = accountDoc.data() as Account;
        const memberScore = accountData.scores?.[memberId]?.currentScore || 0;
        console.log('Retrieved score from Firebase:', memberScore);
        return memberScore;
      }
      
      // If no document exists, return default score
      console.log('No account found, returning default');
      return 0;
    } catch (error) {
      console.error('Error retrieving score from Firebase:', error);
      return 0;
    }
  }

  /**
   * Save the score for a specific member
   */
  static async saveScore(score: number, memberId: string | null = null): Promise<number> {
    try {
      const user = auth.currentUser;
      
      // If no user is signed in, return the score without saving
      if (!user) {
        console.warn('No user signed in, score not saved');
        return score;
      }
      
      // Ensure account exists
      await this.ensureAccountExists();
      
      // If no memberId provided, get the active member or create default
      if (!memberId) {
        const account = await this.getAccount();
        memberId = account?.activeMemberId || await this.ensureDefaultMember();
      }
      
      console.log(`Saving score for user ${user.uid}, member ${memberId}:`, score);
      
      const accountRef = doc(db, 'accounts', user.uid);
      
      // Update the member's score
      await updateDoc(accountRef, {
        [`scores.${memberId}.currentScore`]: score,
        [`scores.${memberId}.lastUpdated`]: new Date()
      });
      
      // Update the account leaderboard
      await this.updateAccountLeaderboard();
      
      console.log('Score saved to Firebase successfully');
      return score;
    } catch (error) {
      console.error('Error saving score to Firebase:', error);
      return score;
    }
  }

  /**
   * Reset the score to zero for a specific member
   */
  static async resetScore(memberId: string | null = null): Promise<number> {
    try {
      const resetScore = 0;
      console.log('Resetting score to:', resetScore);
      
      // Use the saveScore method to reset the score
      return await this.saveScore(resetScore, memberId);
    } catch (error) {
      console.error('Error resetting score in Firebase:', error);
      return 0;
    }
  }

  /**
   * Update the account's internal leaderboard based on all member scores
   */
  private static async updateAccountLeaderboard(): Promise<void> {
    try {
      const user = auth.currentUser;
      if (!user) return;
      
      const account = await this.getAccount();
      if (!account || !account.members || !account.scores) return;
      
      // Create leaderboard entries for all members
      const leaderboardEntries: LeaderboardEntry[] = [];
      
      for (const [memberId, memberInfo] of Object.entries(account.members)) {
        // Skip null members (deleted members)
        if (!memberInfo) continue;
        
        const memberScore = account.scores[memberId]?.currentScore || 0;
        const lastUpdated = account.scores[memberId]?.lastUpdated || new Date();
        
        leaderboardEntries.push({
          memberId,
          name: memberInfo.name,
          score: memberScore,
          lastUpdated
        });
      }
      
      // Sort by score (descending) and add ranks
      leaderboardEntries.sort((a, b) => b.score - a.score);
      leaderboardEntries.forEach((entry, index) => {
        entry.rank = index + 1;
      });
      
      const accountRef = doc(db, 'accounts', user.uid);
      await updateDoc(accountRef, {
        leaderboard: leaderboardEntries,
        leaderboardUpdated: new Date()
      });
      
      console.log('Account leaderboard updated successfully');
    } catch (error) {
      console.error('Error updating account leaderboard:', error);
    }
  }

  /**
   * Get the account's private leaderboard (only shows members within the account)
   */
  static async getLeaderboard(maxEntries: number = 10): Promise<LeaderboardEntry[]> {
    try {
      const user = auth.currentUser;
      if (!user) {
        console.warn('No user signed in, returning empty leaderboard');
        return [];
      }
      
      console.log(`Fetching account leaderboard for user: ${user.uid}`);
      
      const account = await this.getAccount();
      if (!account || !account.leaderboard) {
        console.log('No leaderboard found for account');
        return [];
      }
      
      // Return the account's leaderboard (already sorted and ranked)
      const leaderboard = account.leaderboard.slice(0, maxEntries);
      console.log('Account leaderboard retrieved successfully:', leaderboard);
      return leaderboard;
    } catch (error) {
      console.error('Error retrieving account leaderboard:', error);
      return [];
    }
  }

  /**
   * Get the complete account document
   */
  static async getAccount(): Promise<Account | null> {
    try {
      const user = auth.currentUser;
      if (!user) {
        console.log('DEBUG: No user signed in');
        return null;
      }
      
      console.log('DEBUG: Getting account for user:', user.uid);
      const accountRef = doc(db, 'accounts', user.uid);
      const accountDoc = await getDoc(accountRef);
      
      if (accountDoc.exists()) {
        const data = accountDoc.data() as Account;
        console.log('DEBUG: Account data found:', data);
        return data;
      } else {
        console.log('DEBUG: No account document found for user:', user.uid);
        return null;
      }
    } catch (error) {
      console.error('Error getting account:', error);
      return null;
    }
  }

  /**
   * Ensure the account document exists with basic structure
   */
  static async ensureAccountExists(): Promise<void> {
    try {
      const user = auth.currentUser;
      if (!user) return;
      
      const accountRef = doc(db, 'accounts', user.uid);
      const accountDoc = await getDoc(accountRef);
      
      if (!accountDoc.exists()) {
        console.log('Creating new account document');
        await setDoc(accountRef, {
          accountInfo: {
            email: user.email,
            displayName: user.displayName || user.email || 'Anonymous',
            createdAt: new Date()
          },
          members: {},
          scores: {},
          leaderboard: [],
          activeMemberId: null
        });
      }
    } catch (error) {
      console.error('Error ensuring account exists:', error);
    }
  }

  /**
   * Ensure a default member exists for the account
   */
  static async ensureDefaultMember(): Promise<string | null> {
    try {
      const user = auth.currentUser;
      if (!user) return null;
      
      const account = await this.getAccount();
      if (account && account.members && Object.keys(account.members).length > 0) {
        return Object.keys(account.members)[0];
      }
      
      // Create default member
      const defaultMemberId = `member_${Date.now()}`;
      const defaultMemberName = user.displayName || user.email?.split('@')[0] || 'Member 1';
      
      await this.addMember(defaultMemberName, defaultMemberId);
      await this.setActiveMember(defaultMemberId);
      
      return defaultMemberId;
    } catch (error) {
      console.error('Error ensuring default member:', error);
      return null;
    }
  }

  /**
   * Add a new member to the account
   */
  static async addMember(memberName: string, memberId: string | null = null): Promise<string | null> {
    try {
      const user = auth.currentUser;
      if (!user) return null;
      
      await this.ensureAccountExists();
      
      const newMemberId = memberId || `member_${Date.now()}`;
      const accountRef = doc(db, 'accounts', user.uid);
      
      await updateDoc(accountRef, {
        [`members.${newMemberId}`]: {
          name: memberName,
          createdAt: new Date(),
          isActive: true,
          icon: '😊'
        },
        [`scores.${newMemberId}`]: {
          currentScore: 0,
          lastUpdated: new Date()
        }
      });
      
      // Update leaderboard
      await this.updateAccountLeaderboard();
      
      console.log('Member added successfully:', newMemberId);
      return newMemberId;
    } catch (error) {
      console.error('Error adding member:', error);
      return null;
    }
  }

  /**
   * Remove a member from the account
   */
  static async removeMember(memberId: string): Promise<boolean> {
    try {
      const user = auth.currentUser;
      if (!user) return false;
      
      const account = await this.getAccount();
      if (!account || !account.members || !account.members[memberId]) {
        console.warn('Member not found:', memberId);
        return false;
      }
      
      // Don't allow removing the last member
      if (Object.keys(account.members).length <= 1) {
        console.warn('Cannot remove the last member');
        return false;
      }
      
      const accountRef = doc(db, 'accounts', user.uid);
      
      // Remove member and their score
      const updates: { [key: string]: any } = {};
      updates[`members.${memberId}`] = null;
      updates[`scores.${memberId}`] = null;
      
      // If this was the active member, set a new active member
      if (account.activeMemberId === memberId) {
        const remainingMembers = Object.keys(account.members).filter(id => id !== memberId);
        updates.activeMemberId = remainingMembers[0] || null;
      }
      
      await updateDoc(accountRef, updates);
      
      // Update leaderboard
      await this.updateAccountLeaderboard();
      
      console.log('Member removed successfully:', memberId);
      return true;
    } catch (error) {
      console.error('Error removing member:', error);
      return false;
    }
  }

  /**
   * Get all members for the current account
   */
  static async getMembers(): Promise<{ [key: string]: Member | null }> {
    try {
      const account = await this.getAccount();
      const members = account?.members || {};
      console.log('DEBUG: Members retrieved:', members);
      return members;
    } catch (error) {
      console.error('Error getting members:', error);
      return {};
    }
  }

  /**
   * Set the active member for the account
   */
  static async setActiveMember(memberId: string): Promise<boolean> {
    try {
      const user = auth.currentUser;
      if (!user) return false;
      
      const account = await this.getAccount();
      if (!account || !account.members || !account.members[memberId]) {
        console.warn('Member not found:', memberId);
        return false;
      }
      
      const accountRef = doc(db, 'accounts', user.uid);
      await updateDoc(accountRef, {
        activeMemberId: memberId
      });
      
      console.log('Active member set to:', memberId);
      return true;
    } catch (error) {
      console.error('Error setting active member:', error);
      return false;
    }
  }

  /**
   * Get the currently active member ID
   */
  static async getActiveMember(): Promise<string | null> {
    try {
      const account = await this.getAccount();
      return account?.activeMemberId || null;
    } catch (error) {
      console.error('Error getting active member:', error);
      return null;
    }
  }

  /**
   * Update a member's icon
   */
  static async updateMemberIcon(memberId: string, icon: string): Promise<boolean> {
    try {
      const user = auth.currentUser;
      if (!user) return false;
      
      const account = await this.getAccount();
      if (!account || !account.members || !account.members[memberId]) {
        console.warn('Member not found:', memberId);
        return false;
      }
      
      const accountRef = doc(db, 'accounts', user.uid);
      await updateDoc(accountRef, {
        [`members.${memberId}.icon`]: icon
      });
      
      console.log('Member icon updated successfully:', memberId, icon);
      return true;
    } catch (error) {
      console.error('Error updating member icon:', error);
      return false;
    }
  }

  /**
   * Update a member's name
   */
  static async updateMemberName(memberId: string, name: string): Promise<boolean> {
    try {
      const user = auth.currentUser;
      if (!user) return false;
      
      const account = await this.getAccount();
      if (!account || !account.members || !account.members[memberId]) {
        console.warn('Member not found:', memberId);
        return false;
      }
      
      const accountRef = doc(db, 'accounts', user.uid);
      await updateDoc(accountRef, {
        [`members.${memberId}.name`]: name
      });
      
      // Update the account leaderboard to reflect the name change
      await this.updateAccountLeaderboard();
      
      console.log('Member name updated successfully:', memberId, name);
      return true;
    } catch (error) {
      console.error('Error updating member name:', error);
      return false;
    }
  }
}

export default FirebaseScoreService;
